import { Language } from '../types';

/**
 * Image SEO Helper for Google Images optimization
 * Generates descriptive, keyword-rich, and contextual alt and title attributes in FR, NL, EN.
 */

export interface ImageSeoAttributes {
  alt: string;
  title: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
}

export function getServiceImageSeo(serviceId: string, lang: Language): ImageSeoAttributes {
  switch (serviceId) {
    case 'plumbing':
      return {
        alt: {
          fr: 'Dépannage plomberie d\'urgence, réparation fuite d\'eau sanitaire et robinetterie en Belgique - API HEAT',
          nl: 'Dringende loodgieterij herstelling, lekdetectie en sanitair onderhoud in België - API HEAT',
          en: 'Emergency plumbing repair, water leak detection, and sanitary fixtures in Belgium - API HEAT'
        }[lang],
        title: {
          fr: 'Plombier professionnel agréé - Dépannage en 30 minutes',
          nl: 'Erkend professioneel loodgieter - Spoedinterventie in 30 minuten',
          en: 'Certified master plumber - 30-minute rapid response'
        }[lang],
        loading: 'lazy',
        decoding: 'async'
      };

    case 'heating':
      return {
        alt: {
          fr: 'Entretien chaudière gaz et mazout agréé, dépannage brûleur et attestation légale PEB/Flandre/Wallonie - API HEAT',
          nl: 'Onderhoud gasketel en stookolie met officieel reinigings- en verbrandingsattest - API HEAT',
          en: 'Certified gas and oil boiler maintenance, burner repair, and legal compliance certificates - API HEAT'
        }[lang],
        title: {
          fr: 'Chauffagiste certifié toutes marques (Vaillant, Bulex, Viessmann, Buderus)',
          nl: 'Gecertificeerd verwarmingsmonteur alle merken',
          en: 'Certified heating engineer for all major boiler brands'
        }[lang],
        loading: 'lazy',
        decoding: 'async'
      };

    case 'unblocking':
      return {
        alt: {
          fr: 'Débouchage canalisation haute pression, curage WC et inspection vidéo par caméra endoscopique - API HEAT',
          nl: 'Hogedruk ontstopping van afvoerleidingen, WC en inspectie met endoscopische camera - API HEAT',
          en: 'High-pressure drain jetting, toilet unblocking, and sewer camera inspection - API HEAT'
        }[lang],
        title: {
          fr: 'Hydrocurage et débouchage express 24h/24',
          nl: 'Hogedrukreiniging en spoedontstopping 24/7',
          en: '24/7 emergency hydro-jetting and drain unblocking'
        }[lang],
        loading: 'lazy',
        decoding: 'async'
      };

    case 'ventilation':
      return {
        alt: {
          fr: 'Installation et entretien VMC simple et double flux pour assainir l\'air intérieur - API HEAT',
          nl: 'Installatie en onderhoud mechanische ventilatie VMC voor gezonde binnenlucht - API HEAT',
          en: 'Installation and maintenance of continuous mechanical ventilation systems (VMC) - API HEAT'
        }[lang],
        title: {
          fr: 'Ventilation VMC conforme aux normes énergétiques belges',
          nl: 'Mechanische ventilatie conform Belgische energienormen',
          en: 'VMC ventilation complying with Belgian energy standards'
        }[lang],
        loading: 'lazy',
        decoding: 'async'
      };

    case 'water-softener':
      return {
        alt: {
          fr: 'Installation adoucisseur d\'eau sans calcaire volumétrique et protection des canalisations - API HEAT',
          nl: 'Plaatsing kalkwerende waterontharder en bescherming van leidingen tegen kalkaanslag - API HEAT',
          en: 'Custom water softener installation for limescale elimination and pipe protection - API HEAT'
        }[lang],
        title: {
          fr: 'Adoucisseurs d\'eau haute performance anti-tartre',
          nl: 'Hoogrendement waterontharders tegen kalk',
          en: 'High-performance limescale prevention water softeners'
        }[lang],
        loading: 'lazy',
        decoding: 'async'
      };

    default:
      return {
        alt: {
          fr: 'Services professionnels de plomberie, chauffage et débouchage en Belgique - API HEAT',
          nl: 'Professionele diensten loodgieterij, verwarming en ontstopping in België - API HEAT',
          en: 'Professional plumbing, heating, and drain cleaning services in Belgium - API HEAT'
        }[lang],
        title: {
          fr: 'API HEAT Dépannage 24/7',
          nl: 'API HEAT Spoeddienst 24/7',
          en: 'API HEAT 24/7 Dispatch'
        }[lang],
        loading: 'lazy',
        decoding: 'async'
      };
  }
}

export function getHeroImageSeo(lang: Language): ImageSeoAttributes {
  return {
    alt: {
      fr: 'Plombier chauffagiste professionnel API HEAT en intervention de dépannage d\'urgence 24h/24 en Belgique',
      nl: 'Professionele loodgieter en verwarmingstechnicus API HEAT voor 24/7 spoedinterventies in België',
      en: 'Certified API HEAT plumber and heating technician on 24/7 emergency dispatch across Belgium'
    }[lang],
    title: {
      fr: 'API HEAT - Dépannage Plomberie, Chauffage & Débouchage en 30 min chrono',
      nl: 'API HEAT - Loodgieter, Verwarming & Ontstopping binnen 30 minuten ter plaatse',
      en: 'API HEAT - Emergency Plumbing, Heating & Drain Unblocking within 30 min'
    }[lang],
    loading: 'eager',
    decoding: 'async'
  };
}

export function getZonesBannerImageSeo(lang: Language): ImageSeoAttributes {
  return {
    alt: {
      fr: 'Flotte d\'intervention mobile d\'urgence API HEAT circulant à Bruxelles, en Flandre et en Wallonie',
      nl: 'Mobiele interventievloot van API HEAT actief in Brussel, Vlaanderen en Wallonië',
      en: 'API HEAT mobile emergency intervention fleet covering Brussels, Flanders, and Wallonia'
    }[lang],
    title: {
      fr: 'Déplacements d\'urgence partout en Belgique en moins de 30 minutes',
      nl: 'Snelle interventies in heel België binnen 30 minuten',
      en: 'Emergency dispatches across Belgium in under 30 minutes'
    }[lang],
    loading: 'lazy',
    decoding: 'async'
  };
}

export function getEquipmentBannerImageSeo(lang: Language): ImageSeoAttributes {
  return {
    alt: {
      fr: 'Véhicule d\'outillage professionnel certifié et matériel de recherche de fuite non destructive API HEAT',
      nl: 'Gecertificeerde servicewagen met niet-destructieve lekdetectieapparatuur en gereedschap API HEAT',
      en: 'Certified professional service van and non-destructive leak detection tools by API HEAT'
    }[lang],
    title: {
      fr: 'Équipement thermique et caméras endoscopiques de pointe',
      nl: 'Geavanceerde thermische apparatuur en camera-inspectie',
      en: 'State-of-the-art thermal imaging and endoscopic pipe cameras'
    }[lang],
    loading: 'lazy',
    decoding: 'async'
  };
}

export function getAboutImageSeo(lang: Language): ImageSeoAttributes {
  return {
    alt: {
      fr: 'Artisan chauffagiste et plombier certifié API HEAT ELF DISTRICT lors d\'une installation thermique conforme',
      nl: 'Erkend verwarmingsmonteur en loodgieter API HEAT ELF DISTRICT bij thermische installatie',
      en: 'Certified master plumber and heating installer from API HEAT ELF DISTRICT'
    }[lang],
    title: {
      fr: 'Expertise artisanale & garanties décennales API HEAT (BE0794.514.033)',
      nl: 'Vakmanschap & garantie API HEAT (BE0794.514.033)',
      en: 'Certified craftsmanship & warranties API HEAT (BE0794.514.033)'
    }[lang],
    loading: 'lazy',
    decoding: 'async'
  };
}

export function getContactImageSeo(lang: Language): ImageSeoAttributes {
  return {
    alt: {
      fr: 'Permanence téléphonique et dispatching d\'urgence plomberie chauffage 24/7 API HEAT',
      nl: '24/7 Noodcentrale en telefonische permanentie loodgieterij en verwarming API HEAT',
      en: '24/7 emergency dispatch and telephone assistance center API HEAT'
    }[lang],
    title: {
      fr: 'Assistance immédiate 24/7 - Appel non surtaxé',
      nl: 'Onmiddellijke hulp 24/7 - Direct bereikbaar',
      en: 'Immediate 24/7 assistance - Direct response'
    }[lang],
    loading: 'lazy',
    decoding: 'async'
  };
}

export function getLogoImageSeo(lang: Language): ImageSeoAttributes {
  return {
    alt: {
      fr: 'API HEAT Plomberie Chauffage Débouchage Belgique - Logo Officiel',
      nl: 'API HEAT Loodgieter Verwarming Ontstoppingsdienst België - Officieel Logo',
      en: 'API HEAT Plumbing Heating Drain Cleaning Belgium - Official Logo'
    }[lang],
    title: {
      fr: 'API HEAT 🔴🔵 - Spécialiste thermique & sanitaire en Belgique',
      nl: 'API HEAT 🔴🔵 - Specialist sanitair & verwarming in België',
      en: 'API HEAT 🔴🔵 - Heating & Plumbing Specialists in Belgium'
    }[lang],
    loading: 'eager',
    decoding: 'async'
  };
}
